import React, { useEffect, useState, useMemo } from "react";
import {
  Checkbox,
  Pagination,
  TextField,
  IconButton,
  Box,
  Divider,
} from "@mui/material";
import SearchBar from "../../common/filters/SearchBar";
import Toast from "../../common/Toast";
import DataTable from "../../common/DataTable";
import { ShipmentType } from "../../types/Shipment";
import {
  fetchShipmentListingData,
  updateShipments,
} from "../../api/ShipmentDataAPI";
import { useNavigate } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PencilIcon from "../../assets/pencil-icon.svg";
import { useRole } from "../../context/RoleContext";
import {
  EditableFieldContainer,
  EditableTextFieldStyles,
  EditableDisplayContainer,
  PencilIconButton,
  ShipmentCard,
  OrderDetailsContainer,
  OrderIdText,
  PriceText,
  SummaryButton,
  SummaryButtonContainer,
  FooterContainer,
  FooterText,
  SearchRowContainer,
  OrangeCheckboxStyle,
  LocationText,
} from "./shipment.styles";
import colors from "../../theme/Colours";
import PageHeader from "../../common/PageHeader";
import { Tooltip } from "@mui/material";

const orgId = localStorage.getItem("orgId");

interface EditableFieldProps {
  value: string;
  onSave: (newValue: string) => void;
  onCancel?: () => void;
  field: string;
  isEditing: boolean;
  onEditClick: () => void;
}

export const EditableField: React.FC<EditableFieldProps> = ({
  value,
  onSave,
  onCancel,
  field,
  isEditing,
  onEditClick,
}) => {
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    onSave(editValue);
  };

  const handleCancel = () => {
    setEditValue(value);
    onCancel?.();
  };

  return isEditing ? (
    <EditableFieldContainer>
      <TextField
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        size="small"
        sx={EditableTextFieldStyles}
      />
      <IconButton
        size="small"
        onClick={handleSave}
        aria-label={`Save ${field}`}
      >
        <CheckCircleIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={handleCancel}
        aria-label={`Cancel ${field}`}
      >
        <CancelIcon fontSize="small" />
      </IconButton>
    </EditableFieldContainer>
  ) : (
    <EditableDisplayContainer>
      {value}
      <PencilIconButton size="small" onClick={onEditClick}>
        <img src={PencilIcon} alt="Edit" width={16} height={16} />
      </PencilIconButton>
    </EditableDisplayContainer>
  );
};

const areAllFieldsFilled = (
  orders: ShipmentType[],
  editedFields: Record<string, Partial<ShipmentType>>
) => {
  return orders.every((order) => {
    const editedData = editedFields[order.orderDetails.orderId] || {};

    const truckNo = editedData.truckNo || order.truckNo;
    const driverName = editedData.driverName || order.driverName;
    const phone = editedData.phone || order.phone;

    return truckNo && driverName && phone;
  });
};

const Shipment: React.FC = () => {
  const [shipmentData, setShipmentData] = useState<ShipmentType[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<ShipmentType[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [error, setError] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 6,
    totalRecords: 0,
  });

  const { role } = useRole();

  const [editingField, setEditingField] = useState<{
    id: string;
    field: string;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const [editedFields, setEditedFields] = useState<
    Record<string, Partial<ShipmentType>>
  >({});

  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      {
        label: "",
        field: "checkbox",
        renderCell: (row: ShipmentType) => (
          <Checkbox
            checked={selectedOrders.some(
              (shipment) =>
                shipment.orderDetails.orderId === row.orderDetails.orderId
            )}
            onChange={() => handleCheckboxChange(row)}
            sx={OrangeCheckboxStyle}
          />
        ),
      },
      { label: "Customer", field: "customer" },
      {
        label: "Location",
        field: "location",
        renderCell: (row: ShipmentType) => (
          <Tooltip title={row.location} arrow placement="top">
            <LocationText>{row.location}</LocationText>
          </Tooltip>
        ),
      },
      {
        label: "Order Details",
        field: "orderDetails",
        renderCell: (row: ShipmentType) => (
          <OrderDetailsContainer>
            <OrderIdText variant="body2">
              {row.orderDetails.orderId}
            </OrderIdText>
            <PriceText variant="body2">{row.orderDetails.price}</PriceText>
          </OrderDetailsContainer>
        ),
      },
      { label: "Payment", field: "payment" },
      { label: "ETA", field: "ETA" },
      { label: "Order Status", field: "orderStatus" },
      {
        label: "Truck No",
        field: "truckNo",
        renderCell: (row: ShipmentType) => (
          <EditableField
            value={
              editedFields[row.orderDetails.orderId]?.truckNo ||
              row.truckNo ||
              ""
            }
            field="truckNo"
            isEditing={
              editingField?.id === row.orderDetails.orderId &&
              editingField.field === "truckNo"
            }
            onEditClick={() =>
              setEditingField({
                field: "truckNo",
                id: row.orderDetails.orderId,
              })
            }
            onSave={(newValue) => {
              setEditedFields((prev) => ({
                ...prev,
                [row.orderDetails.orderId]: {
                  ...prev[row.orderDetails.orderId],
                  truckNo: newValue,
                },
              }));
              setEditingField(null);
            }}
            onCancel={() => setEditingField(null)}
          />
        ),
      },
      {
        label: "Driver Name",
        field: "driverName",
        renderCell: (row: ShipmentType) => (
          <EditableField
            value={
              editedFields[row.orderDetails.orderId]?.driverName ||
              row.driverName ||
              ""
            }
            field="driverName"
            isEditing={
              editingField?.id === row.orderDetails.orderId &&
              editingField.field === "driverName"
            }
            onEditClick={() =>
              setEditingField({
                field: "driverName",
                id: row.orderDetails.orderId,
              })
            }
            onSave={(newValue) => {
              setEditedFields((prev) => ({
                ...prev,
                [row.orderDetails.orderId]: {
                  ...prev[row.orderDetails.orderId],
                  driverName: newValue,
                },
              }));
              setEditingField(null);
            }}
            onCancel={() => setEditingField(null)}
          />
        ),
      },
      {
        label: "Phone Number",
        field: "phone",
        renderCell: (row: ShipmentType) => (
          <EditableField
            value={
              editedFields[row.orderDetails.orderId]?.phone || row.phone || ""
            }
            field="phone"
            isEditing={
              editingField?.id === row.orderDetails.orderId &&
              editingField.field === "phone"
            }
            onEditClick={() =>
              setEditingField({ field: "phone", id: row.orderDetails.orderId })
            }
            onSave={(newValue) => {
              setEditedFields((prev) => ({
                ...prev,
                [row.orderDetails.orderId]: {
                  ...prev[row.orderDetails.orderId],
                  phone: newValue,
                },
              }));
              setEditingField(null);
            }}
            onCancel={() => setEditingField(null)}
          />
        ),
      },
    ],
    [selectedOrders, editingField, editedFields]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data, totalRecords } = await fetchShipmentListingData(
          pagination.page,
          pagination.rowsPerPage,
          debouncedSearchTerm
        );
        setShipmentData(data);
        setPagination((prev) => ({
          ...prev,
          totalRecords,
        }));
      } catch (error) {
        console.error("Error fetching shipment data:", error);
        setToastMessage("Failed to load shipment data.");
        setError(true);
        setShowToast(true);
      }
    };
    fetchData();
  }, [pagination.page, pagination.rowsPerPage, debouncedSearchTerm]);
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleCheckboxChange = (row: ShipmentType): void => {
    const allFieldsFilled = areAllFieldsFilled([row], editedFields);

    if (!allFieldsFilled) {
      setToastMessage(
        "All fields (Truck No, Driver Name, Phone) must be filled!"
      );
      setError(true);
      setShowToast(true);
      return;
    }

    const exists = selectedOrders.some(
      (shipment) => shipment.orderDetails.orderId === row.orderDetails.orderId
    );

    if (exists) {
      setSelectedOrders((prev) =>
        prev.filter(
          (shipment) =>
            shipment.orderDetails.orderId !== row.orderDetails.orderId
        )
      );
    } else {
      setSelectedOrders((prev) => [...prev, row]);
    }
  };

  const handleCreateSummary = async (): Promise<void> => {
    const allFieldsFilled = areAllFieldsFilled(selectedOrders, editedFields);

    if (!allFieldsFilled) {
      setToastMessage("Kindly add all the required fields.");
      setError(true);
      setShowToast(true);
      return;
    }

    try {
      const updatedSelectedOrders = selectedOrders.map((order) => ({
        ...order,
        ...editedFields[order.orderDetails.orderId],
      }));

      await updateShipments(updatedSelectedOrders);

      setToastMessage("Shipments updated successfully!");
      setError(false);
      setShowToast(true);

      navigate(`/${role}/shipment/shipment-summary`, {
        state: { selectedOrders: updatedSelectedOrders },
      });
    } catch (error) {
      console.error("Error updating shipment data:", error);
      setToastMessage("Kindly add all the required fields.");
      setError(true);
      setShowToast(true);
    }
  };

  const handleChangePage = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const { page, rowsPerPage } = pagination;
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = Math.min(
    startIndex + rowsPerPage,
    startIndex + shipmentData.length
  );
  const currentRows = shipmentData.slice(startIndex, endIndex);
  const emptyRows = rowsPerPage - currentRows.length;

  return (
    <>
      <PageHeader title="Shipment" />
      <ShipmentCard>
        <SearchRowContainer>
          <Box sx={{ flex: 1 }}>
            <SearchBar handleSearch={handleSearch} searchTerm={searchTerm} />
          </Box>
          <SummaryButtonContainer>
            <SummaryButton
              variant="contained"
              color="primary"
              disabled={selectedOrders.length === 0}
              onClick={handleCreateSummary}
            >
              Create Shipment Summary
            </SummaryButton>
          </SummaryButtonContainer>
        </SearchRowContainer>
        <Divider sx={{ mb: 2 }} />
        <DataTable
          alternateBg
          columns={columns.map((col) => ({
            ...col,
            labelStyle: { fontSize: "14px", color: colors.greyTextColor },
            cellStyle:
              col.field === "customer"
                ? { fontWeight: 600, fontSize: "13px" }
                : { fontSize: "11.63px" },
          }))}
          data={shipmentData}
          emptyStateMessage="No shipments available"
          rowsPerPage={rowsPerPage}
          emptyRows={emptyRows}
        />
        <FooterContainer>
          <FooterText variant="subtitle2">
            Showing data {startIndex + 1} to {endIndex} of{" "}
            {pagination.totalRecords} entries
          </FooterText>
          <Pagination
            count={Math.ceil(pagination.totalRecords / pagination.rowsPerPage)}
            page={pagination.page}
            onChange={(_, newPage) => handleChangePage(newPage)}
            shape="rounded"
            variant="outlined"
          />
        </FooterContainer>
      </ShipmentCard>
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />
    </>
  );
};

export default Shipment;
